import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, ExternalLink, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { BaseSheet } from '@/components/common/BaseSheet';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLinkStripeCustomer, useUnlinkedStripeSubscriptions } from '@/hooks/users/useUserManagement';
import { useUsersWithSubscriptions } from '@/hooks/users/useUsersWithSubscriptions';
import { cn } from '@/lib/utils';
import { UnlinkedStripeSubscription } from '@/services/billing/SubscriptionService';
import { getSubscriptionStatusBadge } from '../utils/userBadges';

interface UnlinkedStripeSubscriptionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatPrice = (subscription: UnlinkedStripeSubscription) => {
  if (subscription.unitAmount === null || !subscription.currency) return null;
  const total = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: subscription.currency.toUpperCase(),
  }).format((subscription.unitAmount * subscription.quantity) / 100);
  return subscription.interval ? `${total} / ${subscription.interval}` : total;
};

const UserPicker = ({
  value,
  onChange,
}: {
  value: { id: string; email: string | null } | null;
  onChange: (user: { id: string; email: string | null }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isFetching } = useUsersWithSubscriptions(
    { search: debouncedSearch || undefined },
    1,
    8,
    'created_at',
    'desc',
    open && !!debouncedSearch,
  );
  const users = open && debouncedSearch ? data?.profiles ?? [] : [];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="min-h-[44px] w-full sm:w-72 justify-between font-normal text-base sm:text-sm"
        >
          <span className="truncate">{value ? value.email : 'Choose an account...'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command filter={() => 1}>
          <CommandInput placeholder="Search by email or name..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {!debouncedSearch ? 'Type to search accounts' : isFetching ? 'Searching...' : 'No account found.'}
            </CommandEmpty>
            <CommandGroup>
              {users.map((user) => (
                <CommandItem
                  key={user.id}
                  value={user.id}
                  onSelect={() => {
                    onChange({ id: user.id, email: user.email });
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value?.id === user.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="min-w-0">
                    <div className="truncate">{user.email}</div>
                    {user.full_name && <div className="text-xs text-gray-500 truncate">{user.full_name}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const UnlinkedSubscriptionCard = ({ subscription }: { subscription: UnlinkedStripeSubscription }) => {
  const [selectedUser, setSelectedUser] = useState(subscription.matchedUser);
  const linkMutation = useLinkStripeCustomer();
  const price = formatPrice(subscription);
  const target = selectedUser ?? subscription.matchedUser;

  const handleLink = () => {
    if (!target || !subscription.customerId) return;
    linkMutation.mutate({ userId: target.id, customerId: subscription.customerId });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">
            {subscription.customerEmail || subscription.customerName || subscription.customerId || 'Unknown customer'}
          </div>
          {subscription.customerName && subscription.customerEmail && (
            <div className="text-sm text-gray-500 truncate">{subscription.customerName}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {getSubscriptionStatusBadge(subscription.status)}
          <a
            href={`https://dashboard.stripe.com/subscriptions/${subscription.subscriptionId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-400 hover:text-genie-primary transition-colors"
            title="Open in Stripe"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
        {price && <span>{price}</span>}
        <span>
          {subscription.quantity} {subscription.quantity === 1 ? 'seat' : 'seats'}
        </span>
        <span>Since {new Date(subscription.createdAt).toLocaleDateString()}</span>
      </div>

      {subscription.matchedUser ? (
        <p className="text-sm text-gray-600">
          Matches <span className="font-medium text-gray-900">{subscription.matchedUser.email}</span>. It hasn't
          synced yet.
        </p>
      ) : (
        subscription.reason && <p className="text-xs text-gray-500">{subscription.reason}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {!subscription.matchedUser && <UserPicker value={selectedUser} onChange={setSelectedUser} />}
        <Button
          type="button"
          onClick={handleLink}
          disabled={!target || !subscription.customerId || linkMutation.isPending}
          className="min-h-[44px] bg-genie-primary hover:bg-genie-primary/90 gap-2"
        >
          {linkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {subscription.matchedUser ? 'Sync now' : 'Link & sync'}
        </Button>
      </div>
    </div>
  );
};

export const UnlinkedStripeSubscriptionsSheet = ({ isOpen, onClose }: UnlinkedStripeSubscriptionsSheetProps) => {
  const { data: subscriptions, isLoading, isFetching, error, refetch } = useUnlinkedStripeSubscriptions(isOpen);

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Unlinked Stripe Subscriptions"
      description="Live Stripe subscriptions that aren't attached to any Help Genie account yet, usually ones sales set up directly in Stripe."
      size="xl"
      footer={
        <div className="flex justify-start gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching} className="gap-2">
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
            <span className="text-sm">Checking every live subscription in Stripe...</span>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            Couldn't load subscriptions from Stripe: {(error as Error).message}
          </div>
        ) : !subscriptions?.length ? (
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-8 flex flex-col items-center text-center gap-2">
              <Sparkles className="h-6 w-6 text-genie-primary" />
              <span className="font-semibold text-gray-900">All caught up</span>
              <span className="text-sm text-gray-600">Every live Stripe subscription belongs to a Help Genie account.</span>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {subscriptions.length} {subscriptions.length === 1 ? 'subscription needs' : 'subscriptions need'} an
              account.
            </p>
            {subscriptions.map((subscription) => (
              <UnlinkedSubscriptionCard key={subscription.subscriptionId} subscription={subscription} />
            ))}
          </>
        )}
      </div>
    </BaseSheet>
  );
};
